import * as THREE from 'three';
import type { System } from '../../systems';
import type { Entity, EntityRegistry } from '../../entities';
import { isCollidable, type Collidable } from './Collidable';
import { SpatialHash } from './SpatialHash';
import type { PlayerShip } from '../ships/PlayerShip';
import { Asteroid } from '../sector/Asteroid';

// 800u cells: comfortably larger than the largest collidable (asteroids cap
// around ~80u radius) so the player almost always queries 1–2 cells per axis,
// and sparse enough that each cell typically holds only a handful of items.
const CELL_SIZE = 800;
const _otherCenter = new THREE.Vector3();
const _shipCenter = new THREE.Vector3();
const _delta = new THREE.Vector3();

export interface CollisionDamageHook {
  /** Called once per collision frame with the impact speed along the contact
   *  normal. The handler decides how much hull damage to apply. */
  (impactSpeed: number, other: Entity & Collidable): void;
}

export interface CollisionSystemOptions {
  readonly onPlayerHit?: CollisionDamageHook;
}

// Below this approach speed contacts are treated as scrapes and dealt no
// damage — keeps drift-bumping a static asteroid from chipping hull.
const DAMAGE_SPEED_THRESHOLD = 14;
// Re-arm timer per other-entity, so a single rock can't tick damage every
// frame while the ship sits inside its broad-phase reach.
const HIT_REARM_SECONDS = 0.45;

export class CollisionSystem implements System {
  readonly id = 'collision';

  private readonly hash = new SpatialHash<Entity & Collidable>(CELL_SIZE);
  private readonly hits: (Entity & Collidable)[] = [];
  private readonly hitCooldowns = new Map<Entity & Collidable, number>();

  constructor(
    private readonly ship: PlayerShip,
    private readonly registry: EntityRegistry,
    private readonly options: CollisionSystemOptions = {}
  ) {}

  update(dt: number): void {
    // Rebuild the grid each frame — incremental updates would be cheaper but
    // belt/ring asteroids and NPCs both move, and the rebuild itself is just
    // ~600 inserts of (vec3, push), which is far cheaper than the O(n) scan
    // it replaces for the player's collision query.
    this.hash.clear();
    for (const entity of this.registry.all().values()) {
      if (entity === this.ship) continue;
      if (!isCollidable(entity)) continue;
      entity.getCollisionCenter(_otherCenter);
      this.hash.insert(entity, _otherCenter);
    }

    const shipRadius = this.ship.collisionRadius;
    this.ship.getCollisionCenter(_shipCenter);

    // Query reach has to cover the largest possible "us + them" sum radius;
    // keep a small slack so the broad-phase never misses a contact.
    const broadRadius = shipRadius + LARGEST_COLLISION_RADIUS;
    this.hits.length = 0;
    this.hash.queryRadius(_shipCenter, broadRadius, this.hits);

    for (const entity of this.hits) {
      entity.getCollisionCenter(_otherCenter);
      _delta.copy(_shipCenter).sub(_otherCenter);

      const sumRadius = shipRadius + entity.collisionRadius;
      const distSq = _delta.lengthSq();
      if (distSq >= sumRadius * sumRadius) continue;

      const dist = Math.sqrt(Math.max(distSq, 1e-6));
      const overlap = sumRadius - dist;
      const normal = _delta.multiplyScalar(1 / dist);

      this.ship.object3D.position.addScaledVector(normal, overlap);

      const velocity = this.ship.velocity;
      const vn = velocity.dot(normal);
      if (vn < 0) {
        const impactSpeed = -vn;
        velocity.addScaledVector(
          normal,
          -vn * (1 + entity.collisionRestitution)
        );

        // Asteroids and other static rocks deal hull damage proportional to
        // the closing speed. NPC ships are excluded — those fights live in
        // CombatSystem so we don't double-up shoot-and-bump damage.
        if (
          this.options.onPlayerHit &&
          entity instanceof Asteroid &&
          impactSpeed > DAMAGE_SPEED_THRESHOLD
        ) {
          const cooldown = this.hitCooldowns.get(entity) ?? 0;
          if (cooldown <= 0) {
            this.options.onPlayerHit(impactSpeed, entity);
            this.hitCooldowns.set(entity, HIT_REARM_SECONDS);
          }
        }
      }
    }

    if (this.hitCooldowns.size > 0) {
      for (const [entity, cooldown] of this.hitCooldowns) {
        const next = cooldown - dt;
        if (next <= 0 || !entity.isAlive) {
          this.hitCooldowns.delete(entity);
        } else {
          this.hitCooldowns.set(entity, next);
        }
      }
    }
  }
}

// Conservative upper bound on collidable radius — the largest collidable in
// the sector is the station (~36u). Padded so future bigger entities can land
// without breaking the broad-phase: precise per-pair tests still run inside,
// this just controls how many cells we scan.
const LARGEST_COLLISION_RADIUS = 100;
